import React, { useEffect, useState } from "react";
import { PageHeader, Card, Tag } from "../components/UI";
import { Save, CheckCircle2, AlertCircle, Radio, Mail, Plus, Trash2, Calendar, Sparkles, Stethoscope } from "lucide-react";
import api, { formatError } from "../lib/api";

const Pill = ({ ok }) => ok ? <Tag tone="emerald"><CheckCircle2 size={11} /> Live</Tag> : <Tag tone="amber"><AlertCircle size={11} /> Pending</Tag>;

export default function Config() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [weights, setWeights] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [newProgram, setNewProgram] = useState({ code: "", name: "", kind: "quarter", start_date: "", end_date: "", fy_code: "FY2026-27" });

  useEffect(() => { (async () => {
    const [a, b, p, w, sp] = await Promise.all([
      api.get("/config"),
      api.get("/integrations/status"),
      api.get("/programs"),
      api.get("/config/engagement-weights"),
      api.get("/config/specialties"),
    ]);
    setCfg(a.data); setStatus(b.data); setPrograms(p.data); setWeights(w.data); setSpecialties(sp.data);
  })(); }, []);

  async function save() {
    setSaving(true);
    try { await api.put("/config", cfg); alert("Configuration saved."); }
    catch (e) { alert(formatError(e)); }
    setSaving(false);
  }

  async function saveWeights() {
    const total = Object.values(weights).reduce((s, v) => s + Number(v || 0), 0);
    if (total !== 100) { alert(`Weights must sum to 100 (got ${total}).`); return; }
    try { await api.put("/config/engagement-weights", weights); alert("Engagement weights saved."); }
    catch (e) { alert(formatError(e)); }
  }

  async function addProgram(e) {
    e.preventDefault();
    try {
      await api.post("/programs", newProgram);
      const { data } = await api.get("/programs"); setPrograms(data);
      setNewProgram({ code: "", name: "", kind: "quarter", start_date: "", end_date: "", fy_code: "FY2026-27" });
    } catch (e) { alert(formatError(e)); }
  }

  async function delProgram(id) {
    if (!window.confirm("Delete program?")) return;
    await api.delete(`/programs/${id}`);
    const { data } = await api.get("/programs"); setPrograms(data);
  }

  async function addSpecialty() {
    const name = window.prompt("New specialty");
    if (!name) return;
    await api.post("/config/specialties", { name });
    const { data } = await api.get("/config/specialties"); setSpecialties(data);
  }

  async function delSpecialty(id) {
    await api.delete(`/config/specialties/${id}`);
    const { data } = await api.get("/config/specialties"); setSpecialties(data);
  }

  if (!cfg || !weights) return <div className="text-slate-400 text-sm">Loading…</div>;

  const weightTotal = Object.values(weights).reduce((s, v) => s + Number(v || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Administration"
        title="Configuration Engine"
        subtitle="Low-code controls for the engine plus a real-time view of every third-party integration powering CBC."
        testId="config-page"
        actions={<button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-config-btn"><Save size={14} /> Save</button>}
      />

      <Card title="Integration providers" subtitle="Live status of every external system wired into ChannelLoyalty.ai">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="surface p-4">
            <div className="flex items-center justify-between"><div className="font-medium flex items-center gap-2"><Radio size={14} className="text-teal-700" /> WhatsApp</div><Pill ok={status?.whatsapp?.configured} /></div>
            <div className="mt-3 space-y-1 text-[12px]">
              <div className="flex justify-between"><span className="text-slate-500">Provider</span><span className="font-medium">{status?.whatsapp?.provider}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">From</span><span className="font-mono">{status?.whatsapp?.from_number || "—"}</span></div>
              <div className="mt-3">
                <div className="small-caps text-slate-400">Preview webhook</div>
                <div className="font-mono text-[10px] break-all bg-slate-50 border border-slate-100 rounded px-1.5 py-1 mt-0.5">{status?.whatsapp?.webhook_preview || "—"}</div>
              </div>
              <div className="mt-2">
                <div className="small-caps text-slate-400">Production webhook</div>
                <div className="font-mono text-[10px] break-all bg-slate-50 border border-slate-100 rounded px-1.5 py-1 mt-0.5">{status?.whatsapp?.webhook_production || "—"}</div>
              </div>
            </div>
          </div>
          <div className="surface p-4">
            <div className="flex items-center justify-between"><div className="font-medium flex items-center gap-2"><Mail size={14} className="text-teal-700" /> SMS</div><Pill ok={status?.sms?.configured} /></div>
            <div className="mt-3 space-y-1 text-[12px]">
              <div className="flex justify-between"><span className="text-slate-500">Provider</span><span className="font-medium">{status?.sms?.provider}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Sender ID</span><span className="font-mono">{status?.sms?.account}</span></div>
              <div className="text-[10.5px] text-slate-500 mt-2">DLT entity &amp; template IDs configured per campaign.</div>
            </div>
          </div>
          <div className="surface p-4">
            <div className="flex items-center justify-between"><div className="font-medium flex items-center gap-2"><Sparkles size={14} className="text-teal-700" /> AI Engine</div><Pill ok={status?.ai?.configured} /></div>
            <div className="mt-3 space-y-1 text-[12px]">
              <div className="flex justify-between"><span className="text-slate-500">Model</span><span className="font-medium">Claude Sonnet 4.5</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Routing</span><span className="font-mono">Emergent Universal Key</span></div>
              <div className="text-[10.5px] text-slate-500 mt-2">Powers AI Loyalty Assistant + insights.</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Programs Manager */}
      <Card title="Programs (Quarterly + Annual)" subtitle="Define active Quarter / Annual programs. Admin can add future quarters (Q2-FY26, Q3-FY26 etc.) as they launch.">
        <div className="space-y-2 mb-4">
          {programs.map((p) => (
            <div key={p.id} className="surface p-3 flex items-center justify-between" data-testid={`program-${p.id}`}>
              <div className="flex items-center gap-3">
                <Calendar size={14} className="text-teal-700" />
                <div>
                  <div className="text-[13px] font-medium">{p.name} <Tag tone={p.kind === "annual" ? "amber" : "teal"}>{p.kind}</Tag></div>
                  <div className="text-[11px] text-slate-500 font-mono">{p.code} · {p.start_date} → {p.end_date} · {p.fy_code}</div>
                </div>
              </div>
              <button onClick={() => delProgram(p.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
            </div>
          ))}
          {programs.length === 0 && <div className="text-[12px] text-slate-400">No programs configured yet.</div>}
        </div>
        <form onSubmit={addProgram} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid="add-program-form">
          <div><label className="small-caps text-slate-500">Code</label><input value={newProgram.code} onChange={(e) => setNewProgram({ ...newProgram, code: e.target.value })} placeholder="Q2-FY26" required /></div>
          <div className="md:col-span-2"><label className="small-caps text-slate-500">Name</label><input value={newProgram.name} onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })} placeholder="Quarter 2 Program" required /></div>
          <div><label className="small-caps text-slate-500">Kind</label><select value={newProgram.kind} onChange={(e) => setNewProgram({ ...newProgram, kind: e.target.value })}><option value="quarter">Quarter</option><option value="annual">Annual</option></select></div>
          <div><label className="small-caps text-slate-500">Start</label><input type="date" value={newProgram.start_date} onChange={(e) => setNewProgram({ ...newProgram, start_date: e.target.value })} required /></div>
          <div><label className="small-caps text-slate-500">End</label><input type="date" value={newProgram.end_date} onChange={(e) => setNewProgram({ ...newProgram, end_date: e.target.value })} required /></div>
          <button type="submit" className="btn-primary flex items-center gap-1 md:col-span-6 justify-center" data-testid="add-program-btn"><Plus size={13} /> Add Program</button>
        </form>
      </Card>

      {/* Engagement Weights */}
      <Card title="Engagement Score Weights" subtitle="Configure the weight of each factor in the physio engagement score (must sum to 100). Score recomputes live on profile open.">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {["recency", "frequency", "monetary", "whatsapp", "certifications"].map((k) => (
            <div key={k}>
              <label className="small-caps text-slate-500 capitalize">{k}</label>
              <div className="flex items-center gap-1">
                <input type="number" min="0" max="100" value={weights[k]} onChange={(e) => setWeights({ ...weights, [k]: Number(e.target.value) })} data-testid={`weight-${k}`} />
                <span className="text-slate-400 text-[11px]">%</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <Tag tone={weightTotal === 100 ? "emerald" : "rose"}>Total: {weightTotal}%</Tag>
          <button onClick={saveWeights} className="btn-primary flex items-center gap-1" data-testid="save-weights-btn"><Save size={13} /> Save Weights</button>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          <b>Recency</b>: orders / 30d · <b>Frequency</b>: orders / 90d · <b>Monetary</b>: pts earned current quarter ·
          <b> WhatsApp</b>: inbound messages · <b>Certifications</b>: training completed.
        </div>
      </Card>

      {/* Specialties manager */}
      <Card title="Specialty Master" subtitle="Configurable specialty options used in Physio Master. Includes Ortho MD, Orthopaedic Surgeon, MBBS, MD, Other HCP and more.">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[12px] text-slate-500">{specialties.length} specialty(ies) configured</div>
          <button onClick={addSpecialty} className="btn-teal !py-1 inline-flex items-center gap-1" data-testid="add-specialty-config-btn"><Plus size={12} /> Add Specialty</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {specialties.map((s) => (
            <div key={s.id} className="tag tag-slate inline-flex items-center gap-2" data-testid={`specialty-${s.id}`}>
              <Stethoscope size={11} /> {s.name}
              <button onClick={() => delSpecialty(s.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Points engine">
          <div><label className="small-caps text-slate-500">Points per rupee</label><input type="number" step="0.01" value={cfg.points_per_rupee} onChange={(e) => setCfg({ ...cfg, points_per_rupee: Number(e.target.value) })} /></div>
          <div className="mt-3"><label className="small-caps text-slate-500">Referral bonus (points)</label><input type="number" value={cfg.referral_bonus} onChange={(e) => setCfg({ ...cfg, referral_bonus: Number(e.target.value) })} /></div>
          <div className="mt-3"><label className="small-caps text-slate-500">Approval threshold (points)</label><input type="number" value={cfg.approval_threshold_points} onChange={(e) => setCfg({ ...cfg, approval_threshold_points: Number(e.target.value) })} /></div>
        </Card>

        <Card title="Communication preferences">
          <div><label className="small-caps text-slate-500">Languages (comma)</label><input value={(cfg.languages || []).join(", ")} onChange={(e) => setCfg({ ...cfg, languages: e.target.value.split(",").map((s) => s.trim()) })} /></div>
          <div className="mt-3"><label className="small-caps text-slate-500">Channels</label><input value={(cfg.channels || []).join(", ")} onChange={(e) => setCfg({ ...cfg, channels: e.target.value.split(",").map((s) => s.trim()) })} /></div>
          <div className="ai-surface mt-4 p-3 text-[12px]">
            All channel credentials are configured at the environment level. Operators can manage cohorts and templates via the Campaign Manager.
          </div>
        </Card>
      </div>
    </div>
  );
}

